<template>
  <v-card class="elevation-3 mx-auto my-4">
    <v-card-text class="pt-4">
      <v-form ref="form" v-model="isFormValid" lazy-validation>
        <!-- Section 1: Project Identifiers -->
        <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
          <v-icon left color="primary">mdi-card-account-details-outline</v-icon> General Overview
        </v-subheader>
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="form.reference_number"
              label="Reference Number"
              outlined
              readonly
              dense
              @change="updateSelectedServiceFields('reference_number')"
              prepend-inner-icon="mdi-pound"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="form.client_reference"
              label="Client Reference"
              outlined
              readonly
              dense
              @change="updateSelectedServiceFields('client_reference')"
              prepend-inner-icon="mdi-account"
            />
          </v-col>
          <!-- <v-col cols="12" sm="4">
            <v-text-field
              v-model="form.emis"
              label="EMIS"
              outlined
              dense
              @change="updateSelectedServiceFields('emis')"
              prepend-inner-icon="mdi-office-building"
            />
          </v-col> -->
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="form.title"
              :label="`${projectLabel} Title *`"
              outlined
              dense
              @change="updateSelectedServiceFields('title')"
              prepend-inner-icon="mdi-text-short"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="form.type"
              :label="`${projectLabel} Type`"
              outlined
              dense
              @change="updateSelectedServiceFields('type')"
              prepend-inner-icon="mdi-shape"
            />
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <!-- Section 2: Financial Details -->
        <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
          <v-icon left color="primary">mdi-cash-multiple</v-icon> Financial Info
        </v-subheader>
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="form.budget_amount"
              label="Budget Amount"
              type="number"
              step="0.01"
              outlined
              dense
              @change="updateSelectedServiceFields('budget_amount')"
              prepend-inner-icon="mdi-currency-usd"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="form.billed_amount"
              label="Billed Amount"
              type="number"
              step="0.01"
              outlined
              dense
              @change="updateSelectedServiceFields('billed_amount')"
              prepend-inner-icon="mdi-receipt"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="form.billing_type"
              :items="billingTypeOptions"
              label="Billing Type"
              outlined
              dense
              @change="updateSelectedServiceFields('billing_type')"
            />
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <!-- Section 3: Schedule & Status -->
        <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
          <v-icon left color="primary">mdi-clock-outline</v-icon> Schedule & Status
        </v-subheader>
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-select
              v-model="form.status"
              :items="statusOptions"
              label="Status"
              outlined
              dense
              @change="updateSelectedServiceFields('status')"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="form.start_date"
              label="Start Date"
              type="date"
              outlined
              dense
              @change="updateSelectedServiceFields('start_date')"
              prepend-inner-icon="mdi-calendar-start"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="form.due_date"
              label="Due Date"
              type="date"
              outlined
              dense
              @change="updateSelectedServiceFields('due_date')"
              prepend-inner-icon="mdi-calendar-clock"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="form.completed_at"
              label="Completed Date"
              type="date"
              outlined
              dense
              @change="updateSelectedServiceFields('completed_at')"
              prepend-inner-icon="mdi-calendar-check"
            />
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <!-- Section 4: Details & Location -->
        <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
          <v-icon left color="primary">mdi-clipboard-text-outline</v-icon>{{ locationLabel }} & Description
        </v-subheader>
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              v-model="form.location"
              :label="locationLabel"
              outlined
              dense
              @change="updateSelectedServiceFields('location')"
              prepend-inner-icon="mdi-map-marker"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="form.description"
              label="Description"
              outlined
              rows="3"
              @change="updateSelectedServiceFields('description')"
            />
          </v-col>
          <!-- <v-col cols="12">
            <v-combobox
              v-model="form.metadata"
              label="Metadata / Error Codes"
              placeholder="Type value and press Enter"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              prepend-inner-icon="mdi-code-json"
              @change="updateSelectedServiceFields('metadata')"
            ></v-combobox>
          </v-col> -->
        </v-row>
      </v-form>
    </v-card-text>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon> Delete {{projectLabel}}?
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete {{ projectLabel }}
          <strong>{{ form.reference_number || form.title || '' }}</strong>? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="handleDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import helpersJs from "@/plugins/helpers";

export default {
  name: 'ProjectDetails',
  props: {
    selectedProject: {
      type: Object,
      default: null,
    },
  },
  computed: {
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getAppMode(){
      return this.$store.getters.getAppMode
    },
    locationLabel(){
      return this.getAppMode === 'projects' ? 'Site' : 'Venue'
    },
    projectLabel(){
      return this.getAppMode === 'projects' ? 'Project' : 'Event'
    },
  },
  data() {
    return {
      projectsTable: "projects",
      isEditing: false,
      isFormValid: true,
      saving: false,
      deleting: false,
      confirmDeleteDialog: false,
      statusOptions: [
        'draft',
        'starting',
        'pending',
        'inspection',
        'in_progress',
        'awaiting_parts',
        'completed',
        'cancelled',
      ],
      billingTypeOptions: ['fixed', 'hourly', 'milestone', 'custom'],
      form: this.getEmptyForm(),
    };
  },
  watch: {
    selectedProject: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          let meta = newVal.metadata;

          if (typeof meta === 'string') {
            try {
              meta = JSON.parse(meta);
            } catch (e) {
              meta = [];
            }
          }

          this.form = {
            ...this.getEmptyForm(),
            ...newVal,
            metadata: Array.isArray(meta) ? meta : (meta ? [meta] : [])
          };
        } else {
          this.form = this.getEmptyForm();
        }
      },
    },
  },
  methods: {
    getEmptyForm() {
      return {
        id: null,
        client_reference: '',
        title: '',
        reference_number: '',
        type: '',
        status: 'draft',
        start_date: null,
        due_date: null,
        completed_at: null,
        budget_amount: 0,
        billed_amount: 0,
        billing_type: 'fixed',
        description: '',
        location: '',
        metadata: [],
        emis: '',
      };
    },
    enableEdit() {
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      if (this.selectedProject) {
        this.form = { ...this.selectedProject };
      }
      this.$emit('cancel');
    },
    async saveChanges() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        this.$emit('update', { ...this.form });
        this.isEditing = false;
      } finally {
        this.saving = false;
      }
    },
    async handleDelete() {
      this.deleting = true;
      try {
        this.$emit('delete', this.form.id);
        this.confirmDeleteDialog = false;
      } finally {
        this.deleting = false;
      }
    },
    async updateSelectedServiceFields(columnName) {
      if (!this.form.id) return;
      
      let payloadValue = this.form[columnName];
      if (columnName === 'metadata' && typeof payloadValue !== 'string') {
        payloadValue = JSON.stringify(payloadValue);
      }

      await this.updateModelSingleRow(this.projectsTable, columnName, this.form.id, payloadValue);
    },
    async updateModelSingleRow(table, column, id, value) {
      this.$store.commit("setLoader", true);
      await helpersJs.updateModelSingleRow(table, column, id, value);
      this.$store.commit("setLoader", false);
      this.$toast.success("Data updated successfully!", { duration: 5000 });
      this.$emit('save', this.form);
    },
  },
};
</script>

<style scoped>
.v-subheader {
  height: 36px;
}
</style>